/**
 * Proposals API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Proposal,
  ProposalCreateData,
  ProposalUpdateData,
  ProposalFilters,
  ProposalTemplate,
} from '@/types'

export const proposalsApi = {
  /**
   * List proposals with filtering and pagination
   */
  async list(params?: PaginationParams & ProposalFilters): Promise<PaginatedResponse<Proposal>> {
    const response = await apiClient.get<PaginatedResponse<Proposal>>('/proposals', { params })
    return response.data
  },

  /**
   * Get proposal by ID
   */
  async get(id: string): Promise<Proposal> {
    const response = await apiClient.get<ApiResponse<Proposal>>(`/proposals/${id}`)
    return response.data.data
  },

  /**
   * Create a new proposal
   */
  async create(data: ProposalCreateData): Promise<Proposal> {
    const response = await apiClient.post<ApiResponse<Proposal>>('/proposals', data)
    return response.data.data
  },

  /**
   * Update a proposal
   */
  async update(id: string, data: ProposalUpdateData): Promise<Proposal> {
    const response = await apiClient.put<ApiResponse<Proposal>>(`/proposals/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a proposal
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/proposals/${id}`)
  },

  /**
   * Send a proposal
   */
  async send(id: string): Promise<Proposal> {
    const response = await apiClient.post<ApiResponse<Proposal>>(`/proposals/${id}/send`)
    return response.data.data
  },

  /**
   * Accept a proposal
   */
  async accept(id: string): Promise<Proposal> {
    const response = await apiClient.post<ApiResponse<Proposal>>(`/proposals/${id}/accept`)
    return response.data.data
  },

  /**
   * Reject a proposal
   */
  async reject(id: string, reason?: string): Promise<Proposal> {
    const response = await apiClient.post<ApiResponse<Proposal>>(`/proposals/${id}/reject`, { reason })
    return response.data.data
  },

  /**
   * Duplicate a proposal
   */
  async duplicate(id: string): Promise<Proposal> {
    const response = await apiClient.post<ApiResponse<Proposal>>(`/proposals/${id}/duplicate`)
    return response.data.data
  },

  /**
   * Get proposal PDF
   */
  async getPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`/proposals/${id}/pdf`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * List proposal templates
   */
  async listTemplates(): Promise<ProposalTemplate[]> {
    const response = await apiClient.get<ApiResponse<ProposalTemplate[]>>('/proposals/templates')
    return response.data.data
  },
}
